if (!customElements.get('kalisa-desktop-menu')) {
  customElements.define('kalisa-desktop-menu', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.details = this.querySelector('details');
      this.trigger = this.querySelector('summary');
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.onResize = () => {
        if (!window.matchMedia('(min-width: 990px)').matches) this.close();
        else if (this.details.open) this.position();
      };
      this.details.addEventListener('toggle', () => {
        this.trigger.setAttribute('aria-expanded', String(this.details.open));
        if (this.details.open) {
          this.position();
          this.previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          this.locked = true;
          if (typeof trapFocus === 'function') trapFocus(this.details, this.querySelector('input'));
        } else this.release();
      });
      this.querySelector('.kdm-close').addEventListener('click', () => this.close());
      this.querySelector('.kdm-backdrop').addEventListener('click', () => this.close());
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); }
      });
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (event) => {
          let next;
          const rtl = getComputedStyle(this).direction === 'rtl';
          if (event.key === 'ArrowRight') next = (index + (rtl ? -1 : 1) + this.tabs.length) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + (rtl ? 1 : -1) + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); this.select(this.tabs[next]); this.tabs[next].focus(); }
        });
      });
      this.setupSearch();
      this.header = this.closest('header');
      this.onHeaderSearchClick = (event) => {
        if (!window.matchMedia('(min-width: 990px)').matches) return;
        const target = event.target instanceof Element ? event.target : event.target.parentElement;
        const searchTrigger = target && target.closest('.header__search summary.header__icon--search');
        if (!searchTrigger || !this.header.contains(searchTrigger)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.position();
        this.details.open = true;
        this.querySelector('input').focus({ preventScroll: true });
      };
      if (this.header) this.header.addEventListener('click', this.onHeaderSearchClick, true);
      window.addEventListener('resize', this.onResize);
    }
    setupSearch() {
      this.searchInput = this.querySelector('#KalisaDesktopSearch');
      this.searchForm = this.querySelector('.kdm-search');
      this.results = this.querySelector('.kdm-results');
      this.status = this.querySelector('.kdm-status');
      this.clearButton = this.querySelector('.kdm-clear');
      this.menuTabs = this.querySelector('.kdm-tabs');
      this.menuContent = this.querySelector('nav');
      this.requestId = 0;
      this.searchInput.addEventListener('input', (event) => {
        if (!event.isComposing) this.updateSearch();
      });
      this.searchInput.addEventListener('compositionend', () => this.updateSearch());
      this.clearButton.addEventListener('click', () => {
        this.searchInput.value = '';
        this.updateSearch();
        this.searchInput.focus();
      });
      this.searchForm.addEventListener('submit', (event) => {
        if (!this.searchInput.value.trim()) {
          event.preventDefault();
          this.searchInput.focus();
        }
      });
    }
    cancelSearch() {
      clearTimeout(this.searchTimer);
      if (this.searchController) this.searchController.abort();
      this.requestId += 1;
    }
    resultsURL(query) {
      const url = new URL(this.searchForm.action, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      return url.href;
    }
    showSearchMessage(message, query) {
      const text = document.createElement('p');
      text.textContent = message;
      const link = document.createElement('a');
      link.className = 'kdm-results-all';
      link.href = this.resultsURL(query);
      link.textContent = this.dataset.viewAll + ' →';
      this.results.replaceChildren(text, link);
      this.status.textContent = message;
    }
    updateSearch() {
      this.cancelSearch();
      const query = this.searchInput.value.trim();
      const active = Boolean(query) && window.matchMedia('(min-width: 990px)').matches;
      this.clearButton.hidden = !query;
      this.menuTabs.hidden = active;
      this.menuContent.hidden = active;
      this.results.hidden = !active;
      this.classList.toggle('kdm-searching', active);
      this.results.replaceChildren();
      this.results.setAttribute('aria-busy', String(active));
      this.status.textContent = '';
      if (!active) return;
      this.showSearchMessage(this.dataset.loading, query);
      const requestId = this.requestId;
      this.searchTimer = setTimeout(() => this.fetchResults(query, requestId), 180);
    }
    async fetchResults(query, requestId) {
      this.searchController = new AbortController();
      const url = new URL(this.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'query,collection,product');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,tag');
      url.searchParams.set('section_id', 'kalisa-desktop-search');
      try {
        const response = await fetch(url.href, { signal: this.searchController.signal });
        if (!response.ok) throw new Error('Search request failed');
        const html = await response.text();
        if (requestId !== this.requestId || query !== this.searchInput.value.trim()) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const content = parsed.querySelector('[data-kdm-results]');
        if (!content) throw new Error('Search results missing');
        this.results.replaceChildren(content);
        this.status.textContent = content.dataset.status;
        } catch (error) {
        if (error.name !== 'AbortError' && requestId === this.requestId) {
          this.showSearchMessage(this.dataset.error, query);
        }
      } finally {
        if (requestId === this.requestId) this.results.setAttribute('aria-busy', 'false');
      }
    }
    position() {
      const header = this.closest('.header-wrapper') || this.closest('.section-header') || this;
      const bottom = Math.max(0, header.getBoundingClientRect().bottom);
      this.style.setProperty('--kdm-top', bottom + 'px');
    }
    select(tab) {
      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
        this.querySelector('#' + item.getAttribute('aria-controls')).hidden = !selected;
      });
    }
    release() {
      if (!this.locked) return;
      document.body.style.overflow = this.previousOverflow || '';
      this.locked = false;
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.trigger);
    }
    close() {
      if (this.searchInput) {
        this.searchInput.value = '';
        this.updateSearch();
      }
      this.details.open = false;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.release();
    }
    disconnectedCallback() {
      this.cancelSearch();
      window.removeEventListener('resize', this.onResize);
      if (this.header) this.header.removeEventListener('click', this.onHeaderSearchClick, true);
      this.release();
    }
  });
}

